import { Card } from "@/src/components/cards";
import { closeDrawer, openDrawer } from "@/src/components/dialogs";
import { Input } from "@/src/components/universalInput";
import {
  Field,
  FolderItem,
  MesureSession,
  Patient,
  RefAnswer,
  Reference,
} from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import { getUserId, jsonAbstract, sleep, uid } from "@/src/utils/utils";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useState } from "react";
import { getStandardConfidentiality } from "./confidentialitiesHook";
import { ScoresList } from "../hospitalisation/scoresCompartment";

export async function openMesureInputs({
  patient,
  parent,
  onConfirm,
}: {
  patient: Patient;
  parent: FolderItem | null | undefined;
  onConfirm: (mesures: RefAnswer[]) => void;
}) {
  let _date: string | null = null;
  let mesures: RefAnswer[] = [];

  await openDrawer({
    title: "Ajouter une mesure",
    additionalClasses: ["big"],
    trailing: (
      <div>
        <Input
          name={""}
          type="date"
          value={_date || moment().format("YYYY-MM-DD")}
          onChange={async (newValue) => {
            _date = newValue;
          }}
        ></Input>
      </div>
    ),
    content: (
      <MesuresInputs
        patient={patient}
        current={mesures}
        onChange={(newMesures) => {
          mesures = newMesures;
        }}
      ></MesuresInputs>
    ),
    onConfirm: async () => {
      let mesureSession: MesureSession = {
        type: "mesureSession",
        patient,
        creator: getUserId(),
        parent,
        mesures,
        confidentiality: getStandardConfidentiality(patient),
        readBy: [getUserId()],
        _date: _date,
      };

      mesureSession?.mesures?.forEach((mesure) => {
        if (!mesure) return;
        mesure._date = _date;
      });

      await apiFetch(
        "patientMesures",
        "addMesureSession",
        {
          mesureSession,
        },
        "Erreur lors de la création de la session de mesures"
      );

      onConfirm(mesures);
    },
  });
}

export async function openMesureAndScoresInputs({
  patient,
  parent,
  onConfirm,
}: {
  patient: Patient;
  parent: FolderItem | null | undefined;
  onConfirm: (mesures: RefAnswer[]) => void;
}) {
  let _date: string | null = null;
  let mesures: RefAnswer[] = [];

  await openDrawer({
    id: "mesureAndScoresInputsDrawer",
    title: "Ajouter une mesure ou un score",
    additionalClasses: ["big"],
    trailing: (
      <div>
        <Input
          name={""}
          type="date"
          value={_date || moment().format("YYYY-MM-DD")}
          onChange={async (newValue) => {
            _date = newValue;
          }}
        ></Input>
      </div>
    ),
    content: (
      <div className="c1 gap20">
        <MesuresInputs
          patient={patient}
          current={mesures}
          onChange={(newMesures) => {
            mesures = newMesures;
          }}
        ></MesuresInputs>
        <div className="bold f16 mtop20">Scores</div>
        <ScoresList
          patient={patient}
          onChange={(newValue) => {
            onConfirm([]);
            closeDrawer("mesureAndScoresInputsDrawer");
          }}
        ></ScoresList>
      </div>
    ),
    onConfirm: async () => {
      let mesureSession: MesureSession = {
        type: "mesureSession",
        patient,
        creator: getUserId(),
        parent,
        mesures,
        confidentiality: getStandardConfidentiality(patient),
        readBy: [getUserId()],
        _date: _date,
      };

      mesureSession?.mesures?.forEach((mesure) => {
        if (!mesure) return;
        mesure._date = _date;
      });

      await apiFetch(
        "patientMesures",
        "addMesureSession",
        {
          mesureSession,
        },
        "Erreur lors de la création de la session de mesures"
      );

      onConfirm(mesures);
    },
  });
}

export function MesuresInputs({
  mesureReferences,
  patient,
  current,
  onChange,
}: {
  mesureReferences?: Reference[];
  patient: Patient;
  current?: RefAnswer[];
  onChange: (newValue: RefAnswer[]) => void;
}) {
  const [calculatedReference, setCalculatedReference] =
    useState<Reference | null>(null);
  let [searchKey, setSearchKey] = useState("");
  let [refAnswers, setRefAnswers] = useState<RefAnswer[]>(current || []);
  let {
    data: references,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["MesuresInputs", searchKey],
    queryFn: () => {
      if (mesureReferences) return mesureReferences;
      let frequent = USER?.get()?.speciality?.frequentReferences?.filter(
        (reference) => reference?.category == "mesure"
      );

      if (frequent?.length) return frequent;
      return apiFetch("references", "loadMany", {
        userId: getUserId(),
        categoriesIds: ["mesure"],
      });
    },
  });

  references = references?.filter((reference) => reference);

  function getFieldValue(
    referenceName: "taille" | "poids" | "imc" | "surface" | "créatinine"
  ) {
    let value = refAnswers.find(
      (refAnswer) =>
        refAnswer?.reference?.name?.toLowerCase()?.trim() ==
        referenceName?.toLowerCase()?.trim()
    )?.fieldAnswers?.[0]?.value;

    return value;
  }

  async function calculateSurface() {
    let surfaceReference = references?.find((reference) => {
      return reference?.name?.toLowerCase()?.trim() == "surface corporelle";
    });
    if (!surfaceReference) return;
    let surfaceField = surfaceReference.sections?.[0]?.fields?.[0];
    if (!surfaceField) return;

    let height = getFieldValue("taille") || 0;
    let weight = getFieldValue("poids") || 0;

    let surfaceValue = parseFloat(
      Math.sqrt((height * weight) / 3600).toString()
    ).toFixed(2);
    if (!surfaceValue) return;

    await onFieldChange(surfaceReference, surfaceField, surfaceValue);
    setCalculatedReference(surfaceReference);
    await sleep(10);
  }

  async function calculateBMI() {
    let reference = references?.find((reference) => {
      return reference?.name?.toLowerCase()?.trim() == "bmi";
    });
    if (!reference) return;
    let field = reference.sections?.[0]?.fields?.[0];
    if (!field) return;

    let height = getFieldValue("taille") || 0;
    let weight = getFieldValue("poids") || 0;
    if (!height && !weight) return;

    let value = parseFloat(
      ((weight / height / height) * 10000)?.toString()
    ).toFixed(2);

    await onFieldChange(reference, field, value);
    setCalculatedReference(reference);
    await sleep(10);
  }

  async function calculateClearanceMDRD() {
    let reference = references?.find((reference) => {
      return reference?.name?.toLowerCase()?.trim().includes("mdrd");
    });
    if (!reference) return;
    let field = reference.sections?.[0]?.fields?.[0];
    console.log("field", field);
    if (!field) return;

    let creatinineMol = ((getFieldValue("créatinine") || 0) / 10) * 88.4;
    if (!creatinineMol) return;
    let age = moment().diff(moment(patient.birth), "years");
    let value = (
      186.3 *
      Math.pow(creatinineMol * 0.0113, -1.154) *
      Math.pow(age, -0.203) *
      (patient.sex == "homme" ? 1 : 0.742)
    ).toFixed(3);

    await onFieldChange(reference, field, value);
    setCalculatedReference(reference);
    await sleep(10);
  }

  async function calculateClearanceCockroft() {
    let reference = references?.find((reference) => {
      return reference?.name?.toLowerCase()?.trim().includes("cockroft");
    });
    if (!reference) return;
    let field = reference.sections?.[0]?.fields?.[0];
    if (!field) return;

    let creatinine = getFieldValue("créatinine") || 0;

    if (!creatinine) return;

    let age = moment().diff(moment(patient.birth), "years");

    let weight = getFieldValue("poids") || 0;
    let value = (
      ((140 - age) / (7.2 * creatinine)) *
      weight *
      (patient.sex == "homme" ? 1 : 0.85)
    ).toFixed(3);

    console.log("field", field);

    await onFieldChange(reference, field, value);
    setCalculatedReference(reference);
    await sleep(10);
  }

  async function onFieldChange(
    reference: Reference,
    field: Field,
    newValue: any
  ) {
    let newRefAnswers = [...refAnswers];
    let refAnswer = newRefAnswers.find(
      (refAnswer) => refAnswer.reference?.id == reference.id
    );

    if (!refAnswer) {
      refAnswer = {
        patient,
        creator: getUserId(),
        reference: reference,
        fieldAnswers: [],
      };
      newRefAnswers.push(refAnswer!);
    }

    let fieldAnswer = refAnswer?.fieldAnswers?.find(
      (fieldAnswer) => fieldAnswer?.field?.id == field.id
    );
    if (fieldAnswer) {
      fieldAnswer.value = newValue;
    } else {
      refAnswer?.fieldAnswers?.push({
        field,
        value: newValue,
        parent: null,
        patient,
        creator: getUserId(),
      });
    }

    refAnswers = newRefAnswers;
    setRefAnswers(newRefAnswers);
    onChange(newRefAnswers);

    let referenceName = reference.name?.toLowerCase()?.trim();
    if (
      referenceName == "taille" ||
      referenceName == "poids" ||
      referenceName == "créatinine"
    ) {
      await calculateSurface();
      await calculateBMI();
      await calculateClearanceMDRD();
      await calculateClearanceCockroft();
    }
  }

  return (
    <div className="grid c3 mc1 gap15 calign">
      {references?.map((reference) => {
        if (!reference) return null;
        let refAnswer = refAnswers.find(
          (refAnswer) => refAnswer.reference?.id == reference.id
        );
        let hasValue = refAnswer?.fieldAnswers?.some((fieldAnswer) => {
          return fieldAnswer?.value;
        });
        return (
          <Card
            key={`${reference.id}${
              calculatedReference?.id == reference.id ? uid() : ""
            }`}
            color={hasValue ? "blue" : "simple"}
            leading={<div></div>}
            title={reference.name}
          >
            <div className="grid c1 gap5 mtop10">
              {reference.sections?.map((section) => {
                if (!section) return null;
                return (
                  <div key={section.id} className="c1 gap10">
                    {reference.sections!.length > 1 ? (
                      <div>{section.name}</div>
                    ) : null}
                    <div className="grid gap10" style={{ alignItems: "end" }}>
                      {section.fields?.map((field) => {
                        if (!field) return null;

                        if (field.name == "Nouvel élément") return null;
                        let fieldAnswer = refAnswer?.fieldAnswers?.find(
                          (fieldAnswer) => fieldAnswer?.field?.id == field.id
                        );

                        return (
                          <Input
                            key={field.id}
                            name={
                              section.fields!.length > 1 ? field.name : null
                            }
                            unitName={field.unit?.name}
                            value={fieldAnswer?.value}
                            type={jsonAbstract(field.type)}
                            options={field?.options
                              ?.filter((a) => a)
                              ?.map((option) => {
                                return {
                                  value: option!.id,
                                  label: option!.name,
                                };
                              })}
                            onChange={async (newValue) => {
                              await onFieldChange(reference, field, newValue);
                            }}
                          ></Input>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
